import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';

import { uniq, uniqBy } from 'lodash';

import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';

import IconButton from 'components/common/IconButton';
import Badge from 'components/common/Badge';
import Link from 'components/common/Link';
import IconArrowHeadBottom from 'components/icons/IconArrowHeadBottom';
import IconDocument from 'components/icons/IconDocument';

import formatUtcToLocalDate from 'utils/formatUtcToLocalDate';
import formatPrice from 'utils/formatPrice';

import dateFormat from 'config/dateFormat';
import timeDateFormat, { dateTimeFormat } from 'config/timeDateFormat';

import pxToRem from 'styles/pxToRem';

import { getEraDocument } from 'redux/modules/eraStatistics';

import ClaimLogViewTableRowItem from './ClaimLogViewTableRowItem';

const styles = theme => ({
  iconArrowHeadBottom: {
    width: 12,
    height: 8,
  },
  iconArrowHeadBottomExpanded: {
    transform: 'rotate(180deg)',
  },
  buttonExpand: {
    margin: -14,
  },
  rowFullWidth: {
    height: 'auto',
  },
  cellFullWidth: {
    padding: 0,
    border: 'none',
    '&:last-child': {
      padding: 0,
    },
  },
  details: {
    display: 'block',
    padding: 0,
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
    background: theme.palette.grey[100],
  },
  cellInsurance: {
    whiteSpace: 'nowrap',
  },
  expansionPanelClickable: {
  },
  insuranceName: {
    whiteSpace: 'normal',
  },
  cellStatus: {
    textTransform: 'capitalize',
    fontWeight: 'bold',
  },
  cellStatusSent: {
    color: theme.palette.notification['500'],
  },
  cellStatusSuccess: {
    color: theme.palette.add['500'],
  },
  cellStatusError: {
    color: theme.palette.validation['500'],
  },
  typographyOrderLinesCount: {
    display: 'inline-block',
    fontSize: pxToRem(13),
    width: 10,
  },
  cellSubmitDate: {
    width: 79,
  },
  cellCount: {
    borderRight: `1px solid ${theme.palette.primary.A100}`,
    paddingRight: 0,
  },
  iconEra: {
    position: 'relative',
    display: 'inline-block',
    verticalAlign: 'middle',
    marginLeft: 4,
    marginBottom: -3,
    marginTop: -5,
  },
  iconEraDocument: {
    width: 18,
    height: 18,
    color: theme.palette.grey[600],
  },
  iconEraText: {
    display: 'block',
    position: 'absolute',
    paddingLeft: 2,
    paddingRight: 2,
    bottom: 0,
    right: 0,
    fontSize: pxToRem(7),
    fontWeight: 700,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary[500],
    textShadow: 'none',
  },
  eraItem: {
    '& + $eraItem': {
      marginTop: 6,
    },
  },
  linkEra: {
    whiteSpace: 'nowrap',
  },
});

class ClaimLogViewTableRow extends Component {
  constructor(props) {
    super(props);

    this.state = {
      expanded: false,
    };

    this.onTableRowClick = this.onTableRowClick.bind(this);
    this.onExpandButtonClick = this.onExpandButtonClick.bind(this);
  }

  onTableRowClick(event) {
    const { classes } = this.props;
    if (event.target.classList.contains(classes.expansionPanelClickable)) {
      this.setState({ expanded: !this.state.expanded });
    }
  }

  onExpandButtonClick() {
    this.setState({ expanded: !this.state.expanded });
  }

  render() {
    const { props } = this;
    const { classes } = this.props;
    const { expanded } = this.state;
    const charge = props['Submitted Amount'] ? formatPrice(props['Submitted Amount']) : '';
    const currentStatus = props.Status && props.Status.length > 0
      ? props.Status[props.Status.length - 1]
      : null;
    const colSpan = props.variant === 'order' ? 12 : 11;
    const ERAs = props['ERA Claim'] && props['ERA Claim'].length > 0
      ? uniqBy(props['ERA Claim'].reduce((accumulator, ERAClaimItem) => [
        ...accumulator,
        ...ERAClaimItem.ERA,
      ], []), 'ID')
      : [];
    return (
      <Fragment>
        <TableRow
          className={classes.expansionPanelClickable}
          onClick={this.onTableRowClick}
          hover
        >
          <TableCell className={classNames(classes.cellCount, classes.expansionPanelClickable)}>
            {(props['Claim Line'] && props['Claim Line'].length > 0) &&
              <Fragment>
                <Typography
                  className={classNames(classes.expansionPanelClickable,
                    classes.typographyOrderLinesCount)}
                >
                  {props['Claim Line'].length}
                </Typography>
                <IconButton
                  className={classes.buttonExpand}
                  onClick={this.onExpandButtonClick}
                >
                  <IconArrowHeadBottom
                    className={classNames(classes.iconArrowHeadBottom, {
                      [classes.iconArrowHeadBottomExpanded]: expanded,
                    })}
                  />
                </IconButton>
              </Fragment>
            }
          </TableCell>
          <TableCell className={classNames(classes.expansionPanelClickable, classes.cellSubmitDate)}>
            {formatUtcToLocalDate(
              props['Create At'],
              timeDateFormat,
              dateTimeFormat,
              props.business['Company Address']['Time Zone'],
              props.business['Company Address']['Observes Daylight Saving'],
            )}
          </TableCell>
          <TableCell
            className={classNames(classes.cellStatus, classes.expansionPanelClickable, {
              [classes.cellStatusSent]: currentStatus && currentStatus.Category === 'Sent',
              [classes.cellStatusSuccess]: currentStatus && currentStatus.Category === 'Success',
              [classes.cellStatusError]: currentStatus && currentStatus.Category === 'Error',
            })}
          >
            <Link
              href={`/claims/claim-md?ID=${props.ID}`}
              target="_blank"
              breakWord
            >
              {props['Claim Status']}
              {currentStatus ? ` ${currentStatus.Status}` : ''}
            </Link>
          </TableCell>
          {props.variant === 'order' &&
            <TableCell className={classes.expansionPanelClickable}>
              {props['Claim Line'].map(item => item['Hcpc Code']).join(', ')}
            </TableCell>
          }
          <TableCell className={classes.expansionPanelClickable}>
            {(props['Order Line'] && props['Order Line'].length > 0) &&
              uniq(props['Order Line'].map(item => `${item['Date of Service From']}-${item['Date of Service To']}`))
                .map(item => (
                  <div
                    key={item}
                    className={classes.expansionPanelClickable}
                  >
                    {item}
                  </div>
                ))}
            {(props['Claim Line'] && props['Claim Line'].length > 0
              && props['Claim Line'][0]['Date of Service From']) &&
              uniq(props['Claim Line'].map(item => `${item['Date of Service From']}-${item['Date of Service To']}`))
                .map(item => (
                  <div
                    key={item}
                    className={classes.expansionPanelClickable}
                  >
                    {item}
                  </div>
                ))}
          </TableCell>
          <TableCell
            className={classNames(classes.cellInsurance, classes.expansionPanelClickable)}
          >
            {props.Insurance['Payer Name'] &&
              <Fragment>
                <Badge
                  color="primary"
                  tooltipText={props.Insurance['Order Benefits']}
                >
                  {props.Insurance['Order Benefits'] === 'Primary' && '1'}
                  {props.Insurance['Order Benefits'] === 'Secondary' && '2'}
                  {props.Insurance['Order Benefits'] === 'Secondary As Primary' && '2'}
                  {props.Insurance['Order Benefits'] === 'Tertiary' && '3'}
                </Badge>
                {' '}
                <span className={classes.insuranceName}>
                  {props.Insurance['Payer Name']}
                </span>
              </Fragment>
            }
          </TableCell>
          <TableCell className={classes.expansionPanelClickable}>
            {charge}
          </TableCell>
          <TableCell className={classes.expansionPanelClickable}>
            {props['ERA Claim'] && props['ERA Claim'].length > 0
              ? formatPrice(props['ERA Claim'].reduce((accumulator, ERAItem) =>
                accumulator + (ERAItem.Paid || 0), 0)) : ''}
          </TableCell>
          <TableCell className={classes.expansionPanelClickable}>
            {(props['Change Code'] && props['Change Link']) &&
              <Link
                color="primary"
                href={props['Change Link']}
                target="_blank"
                breakWord
              >
                {props['Change Code']}
              </Link>
            }
            {(props['Change Code'] && !props['Change Link']) &&
              props['Change Code']
            }
            {props['Claim Md Code']}
          </TableCell>
          <TableCell className={classes.expansionPanelClickable}>
            {ERAs.length > 0
              ? ERAs.map(ERAItem => (
                <div key={ERAItem.ID} className={classes.eraItem}>
                  <Link
                    className={classes.linkEra}
                    onClick={() => {
                      this.props.actions.getEraDocument({
                        'ERA ID': ERAItem['ERA ID'],
                      });
                    }}
                    target="_blank"
                    breakWord
                  >
                    {ERAItem['Check Number']}
                    <div className={classes.iconEra}>
                      <IconDocument className={classes.iconEraDocument} />
                      <span className={classes.iconEraText}>ERA</span>
                    </div>
                  </Link>
                </div>
              )) : ''}
          </TableCell>
          <TableCell className={classes.expansionPanelClickable}>
            {ERAs.length > 0
              ? ERAs.map(ERAItem => (
                <div key={ERAItem.ID} className={classes.eraItem}>
                  {moment(ERAItem['Paid Date'], 'MM/DD/YYYY').format(dateFormat)}
                </div>
              ))
              : ''}
          </TableCell>
          <TableCell className={classes.expansionPanelClickable}>
            {(props['Send User'] && props['Send User'].ID) &&
              `${props['Send User']['First Name']} ${props['Send User']['Last Name']}`}
          </TableCell>
        </TableRow>
        {(props['Claim Line'] && props['Claim Line'].length > 0 && expanded) &&
          <TableRow className={classes.rowFullWidth}>
            <TableCell
              colSpan={colSpan}
              className={classes.cellFullWidth}
            >
              <div className={classes.details}>
                <Table>
                  <TableBody>
                    {props['Claim Line'].map((item, index) =>
                      <ClaimLogViewTableRowItem
                        key={`${item.ID}-details`}
                        showHead={index === 0}
                        {...item}
                        {...props['Order ID'] ? { 'Order ID': props['Order ID'] } : {}}
                      />)}
                  </TableBody>
                </Table>
              </div>
            </TableCell>
          </TableRow>
        }
      </Fragment>
    );
  }
}

ClaimLogViewTableRow.propTypes = {
  ID: PropTypes.number.isRequired,
  'Create At': PropTypes.string.isRequired,
  'Change Code': PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  'Change Link': PropTypes.string,
  Insurance: PropTypes.shape({
    'Payer Name': PropTypes.string.isRequired,
    'Order Benefits': PropTypes.string.isRequired,
  }).isRequired,
  Extra: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    error: PropTypes.object,
  }),
  business: PropTypes.shape({
    ID: PropTypes.number,
    'Time Zone': PropTypes.string,
  }),
  variant: PropTypes.oneOf(['order', 'order-line']),
  classes: PropTypes.shape({
    expansionPanelClickable: PropTypes.string.isRequired,
    iconArrowHeadBottom: PropTypes.string.isRequired,
    iconArrowHeadBottomExpanded: PropTypes.string.isRequired,
    buttonExpand: PropTypes.string.isRequired,
    rowFullWidth: PropTypes.string.isRequired,
    cellFullWidth: PropTypes.string.isRequired,
    details: PropTypes.string.isRequired,
  }).isRequired,
};

ClaimLogViewTableRow.defaultProps = {
  Extra: null,
  'Change Code': null,
  'Change Link': null,
  business: {},
  variant: 'order-line',
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ getEraDocument }, dispatch),
});

export default connect(null, mapDispatchToProps)(withStyles(styles)(ClaimLogViewTableRow));



// WEBPACK FOOTER //
// ./src/components/workflow/ClaimLogViewDialog/ClaimLogViewTableRow.js